import {
  FormItemLabel,
  NutritionGroupInputsWrapper,
  NutritionAndPackageFoodFormContent,
} from "./styled"
import Button from "components/Buttons/Button"
import useScannedPackageFoodForm from "features/food/hooks/scanResultFoods/useAIScanPackageFoodForm"
import { Fragment, useState } from "react"
import Header from "components/Layouts/Header"
import NutritionFormItem from "./NutritionFormItem"
import ImageUploader from "../ImageUploader"
import { useAppSelector } from "app/hooks"
import { useRevertFoodScanToOriginalImage } from "features/meal/hooks/useScannedImageUploader"
import AppLoading from "components/loading/AppLoading"
import PortionWithUnitTextField from "components/portion/PortionWithUnitTextField"
import AIComment from "../AIComment"
import TextAreaFieldFormItem from "components/inputs/TextAreaFieldFormItem"
import PopConfirm from "components/PopConfirm"
import {
  IAIScanNutritionFactOption,
  IAIScanPackageFoodOption,
} from "model/food/types/AIScan.types"
import AppStyleCtaBox from "components/Common/AppStyleCtaBox"
import { MEAL_MACRO_DECIMAL_PLACE } from "config"

interface IProps {
  food: IAIScanPackageFoodOption | IAIScanNutritionFactOption
  onUpdateFood: (
    updatedFood: IAIScanPackageFoodOption | IAIScanNutritionFactOption,
  ) => void
  onBack: () => void
}

const AIScanPackageFoodDetails = (props: IProps) => {
  const { food: initFood, onUpdateFood, onBack } = props
  const [isPortionValid, setIsPortionValid] = useState(true)

  const {
    onCaloriesChange,
    onNameChange,
    onPortionChange,
    food,
    onProteinChange,
    onFatChange,
    onCarbohydrateChange,
    onUnitChange,
    isFoodChanged,
  } = useScannedPackageFoodForm(initFood)

  const handleUpdateFood = () => {
    const updatedFood: IAIScanPackageFoodOption | IAIScanNutritionFactOption = {
      ...initFood,
      name: food.name,
      portion: food.portion,
      unit: food.unit,
      calorie: food.calorie,
      protein: food.protein,
      fat: food.fat,
      carbohydrate: food.carbohydrate,
      gram: food.gram,
      imageId: undefined,
      previewImageUrl: "",
    }

    onUpdateFood(updatedFood)
  }

  const onRevertFoodScanToOriginalImage = useRevertFoodScanToOriginalImage(
    initFood.id,
  )

  const processScannedFoodImageLoading = useAppSelector(
    (state) => state.meal.processScannedFoodImageLoading,
  )

  const [isPopConfirmDiscardOpen, setIsPopConfirmDiscardOpen] = useState(false)

  const handleBack = () => {
    onRevertFoodScanToOriginalImage()
    onBack()
  }
  return (
    <Fragment>
      <PopConfirm
        isOpen={isPopConfirmDiscardOpen}
        title="Unsaved changes?"
        cancelText="Discard"
        confirmText="Save changes"
        onCancel={handleBack}
        onConfirm={handleUpdateFood}
        loading={false}
      >
        <p style={{ textAlign: "center" }}>
          You haven't saved your meal yet. Are you sure you want to leave?
        </p>
      </PopConfirm>

      <Header
        isAppPaddingStyle
        disabled={processScannedFoodImageLoading}
        title=""
        onBack={() => {
          if (isFoodChanged()) {
            setIsPopConfirmDiscardOpen(true)
          } else if (!processScannedFoodImageLoading) {
            handleBack()
          }
        }}
      />

      <NutritionAndPackageFoodFormContent>
        <TextAreaFieldFormItem
          label="Name"
          placeholder="Name your meal"
          value={food.name}
          onChange={onNameChange}
        />

        <AIComment comment={initFood.comment} />

        <PortionWithUnitTextField
          portionValue={food.portion}
          unitValue={food.unit}
          onUnitChange={onUnitChange}
          onPortionChange={onPortionChange}
          setIsPortionValid={setIsPortionValid}
          calories={food.calorie}
          gram={food.gram}
        />
        <NutritionGroupInputsWrapper>
          <NutritionFormItem
            label="Calories"
            detailTrailing="cal"
            value={food.calorie}
            onChange={onCaloriesChange}
            setIsFormValid={setIsPortionValid}
            paddingRightOffset={10}
          />
          <NutritionFormItem
            label="Protein"
            detailTrailing="g"
            value={food.protein}
            onChange={onProteinChange}
            setIsFormValid={setIsPortionValid}
            decimalPlaces={MEAL_MACRO_DECIMAL_PLACE}
          />
          <NutritionFormItem
            label="Fat"
            detailTrailing="g"
            value={food.fat}
            onChange={onFatChange}
            setIsFormValid={setIsPortionValid}
            decimalPlaces={MEAL_MACRO_DECIMAL_PLACE}
          />
          <NutritionFormItem
            label="Carbs"
            detailTrailing="g"
            value={food.carbohydrate}
            onChange={onCarbohydrateChange}
            setIsFormValid={setIsPortionValid}
            decimalPlaces={MEAL_MACRO_DECIMAL_PLACE}
          />
        </NutritionGroupInputsWrapper>
        <div>
          <FormItemLabel>
            Image <span>{"(optional)"}</span>
          </FormItemLabel>
          <ImageUploader foodId={initFood.id} />
        </div>
      </NutritionAndPackageFoodFormContent>

      <AppStyleCtaBox>
        <Button
          disabled={!isPortionValid || processScannedFoodImageLoading}
          $type="primary"
          onClick={handleUpdateFood}
          style={{ width: "100%" }}
        >
          Save
        </Button>
      </AppStyleCtaBox>

      {processScannedFoodImageLoading && (
        <AppLoading content="Processing image" />
      )}
    </Fragment>
  )
}

export default AIScanPackageFoodDetails
